import { useState, useEffect, useCallback } from 'react';
import { useAtom } from 'jotai';
import {
  remote_getProjects,
  remote_getMyProjects,
  remote_getProjectsSharedWithMe,
} from '~/common/typescript/apis/ProjectAPIs';
import type { Project_Base } from '~/common/typescript/models/Project';
import { userACRoles } from '~/stores/UserStore';
import { AC_TLGeo_Role } from '~/common/typescript/services/access_control/AC_Tlgeo';

interface UseProjectsReturn {
  projects: Project_Base[];
  loading: boolean;
  error: Error | null;
  refetch: () => void;
  isAdmin: boolean;
}

/**
 * Hook to fetch project list for filtering.
 * For admin: fetches all projects.
 * For regular users: fetches user's owned and shared projects (avoids 403 Forbidden).
 */
export const useProjects = (): UseProjectsReturn => {
  const [projects, setProjects] = useState<Project_Base[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const [roles] = useAtom(userACRoles);
  const isAdmin = Array.isArray(roles) && roles.includes(AC_TLGeo_Role.TLGEO_ADMIN);

  const fetchProjects = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      if (isAdmin) {
        // TLGeo Admin can load all system projects
        try {
          const response = await remote_getProjects({
            page: 1,
            pageSize: 1000,
          });
          setProjects(response.results || []);
          return;
        } catch (adminErr) {
          console.warn('Failed to load all projects as admin, falling back to personal projects:', adminErr);
        }
      }

      // Regular user (or fallback): fetch user's owned and shared projects
      const [myProjects, sharedACProjects] = await Promise.all([
        remote_getMyProjects().catch((err) => {
          console.warn('Failed to get my projects:', err);
          return [] as Project_Base[];
        }),
        remote_getProjectsSharedWithMe().catch((err) => {
          console.warn('Failed to get shared projects:', err);
          return [];
        }),
      ]);

      const sharedProjects = (Array.isArray(sharedACProjects) ? sharedACProjects : [])
        .map((ac) => ac?.project)
        .filter((p): p is Project_Base => Boolean(p && p.id));

      const map = new Map<number, Project_Base>();
      (Array.isArray(myProjects) ? myProjects : []).forEach((p) => {
        if (p && p.id) {
          map.set(p.id, p);
        }
      });
      sharedProjects.forEach((p) => {
        if (p && p.id) {
          map.set(p.id, p);
        }
      });

      setProjects(Array.from(map.values()));
    } catch (err) {
      console.error('Failed to fetch accessible projects:', err);
      setError(err as Error);
      setProjects([]);
    } finally {
      setLoading(false);
    }
  }, [isAdmin]);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  return {
    projects,
    loading,
    error,
    refetch: fetchProjects,
    isAdmin,
  };
};
